import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';

import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { TripData } from '../../../models/billing.model';

interface BillData {
  billId: string;
  generatedAt: string;
  trips: TripData[];
  vendor: any;
  subtotal: number;
  addons: any[];
  discounts: any[];
  totalAmount: number;
}

@Component({
  selector: 'app-generated-bill',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    PageHeaderComponent,
  ],
  templateUrl: './generated-bill.component.html',
  styleUrls: ['./generated-bill.component.scss'],
})
export class GeneratedBillComponent implements OnInit {
  @ViewChild('printContainer', { static: false }) printContainer!: ElementRef;

  billData: BillData | null = null;
  displayedColumns: string[] = [
    'routeName',
    'driver',
    'date',
    'distance',
    'rate',
    'amount',
  ];

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Get data from router state using history.state
    const state = history.state;
    if (state && state.billData) {
      this.billData = state.billData;
      console.log(
        'Generated bill - Data received from router state:',
        this.billData
      );
    } else {
      // Fallback: try to get from query params (for backward compatibility)
      this.route.queryParams.subscribe((params) => {
        const billDataParam = params['billData'];

        if (billDataParam) {
          try {
            this.billData = JSON.parse(decodeURIComponent(billDataParam));
            console.log(
              'Generated bill - Data received from query params:',
              this.billData
            );
          } catch (error) {
            console.error('Error parsing bill data:', error);
            this.billData = null;
          }
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/BillingManagement']);
  }

  printBill(): void {
    if (!this.printContainer) return;

    const printContent = this.printContainer.nativeElement.innerHTML;
    const originalContent = document.body.innerHTML;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Bill #${this.billData?.billId}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white; 
              }
              .bill-content { 
                max-width: 800px; 
                margin: 0 auto; 
                background: white; 
              }
              .invoice-header { 
                padding: 20px; 
                border-bottom: 2px solid #f1f3f4; 
                display: flex; 
                justify-content: space-between; 
                align-items: flex-start; 
              }
              .invoice-title h2 { 
                margin: 0 0 16px 0; 
                font-size: 24px; 
                font-weight: 700; 
                color: #333; 
              }
              .invoice-details .bill-id { 
                font-size: 16px; 
                font-weight: 600; 
                color: #1976d2; 
                margin-bottom: 4px; 
              }
              .invoice-details .bill-date { 
                font-size: 12px; 
                color: #666; 
              }
              .bill-to h3 { 
                margin: 0 0 12px 0; 
                font-size: 14px; 
                font-weight: 600; 
                color: #333; 
              }
              .vendor-info .vendor-name { 
                font-size: 16px; 
                font-weight: 600; 
                color: #333; 
                margin-bottom: 4px; 
              }
              .vendor-info .service-type { 
                font-size: 12px; 
                color: #666; 
              }
              .trip-details-section { 
                padding: 20px; 
              }
              .trip-details-section h3 { 
                margin: 0 0 16px 0; 
                font-size: 18px; 
                font-weight: 600; 
                color: #333; 
              }
              .trip-table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-bottom: 20px; 
              }
              .trip-table th { 
                background-color: #f8f9fa; 
                padding: 12px 8px; 
                text-align: left; 
                font-weight: 600; 
                color: #333; 
                border-bottom: 2px solid #dee2e6; 
                font-size: 12px; 
              }
              .trip-table td { 
                padding: 12px 8px; 
                border-bottom: 1px solid #f1f3f4; 
                color: #333; 
                font-size: 12px; 
              }
              .cost-summary-section { 
                padding: 20px; 
                background-color: #f8f9fa; 
              }
              .summary-item { 
                display: flex; 
                justify-content: space-between; 
                align-items: center; 
                padding: 8px 0; 
                border-bottom: 1px solid #e9ecef; 
              }
              .summary-item:last-child { 
                border-bottom: none; 
              }
              .summary-item .label { 
                font-weight: 500; 
                color: #333; 
                font-size: 12px; 
              }
              .summary-item .value { 
                font-weight: 600; 
                color: #333; 
                font-size: 12px; 
              }
              .summary-item .discount-value { 
                color: #dc3545; 
              }
              .summary-item.total-amount { 
                margin-top: 12px; 
                padding-top: 12px; 
                border-top: 2px solid #dee2e6; 
              }
              .summary-item.total-amount .label { 
                font-weight: 700; 
                font-size: 14px; 
                color: #333; 
              }
              .summary-item.total-amount .value { 
                font-weight: 700; 
                font-size: 16px; 
                color: #28a745; 
              }
              .payment-details-section { 
                padding: 20px; 
              }
              .payment-details-section h3 { 
                margin: 0 0 16px 0; 
                font-size: 18px; 
                font-weight: 600; 
                color: #333; 
              }
              .payment-info { 
                display: grid; 
                grid-template-columns: repeat(2, 1fr); 
                gap: 12px; 
              }
              .payment-item { 
                display: flex; 
                justify-content: space-between; 
                align-items: center; 
                padding: 8px 12px; 
                background: #f8f9fa; 
                border-radius: 4px; 
                border: 1px solid #e9ecef; 
              }
              .payment-item .label { 
                font-weight: 500; 
                color: #666; 
                font-size: 12px; 
              }
              .payment-item .value { 
                font-weight: 600; 
                color: #333; 
                font-size: 12px; 
              }
              @media print {
                body { margin: 0; padding: 0; }
                .bill-content { box-shadow: none; border: none; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toLocaleString()}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  }

  get totalTrips(): number {
    return this.billData?.trips?.length || 0;
  }

  get vendorName(): string {
    return this.billData?.vendor?.organizationName || 'N/A';
  }

  get bankDetails(): any {
    return this.billData?.vendor?.bankDetails || {};
  }
}
